import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
    Autocomplete, Box, Button, Checkbox, FormControlLabel, IconButton,
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
    TableRow, TextField, Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DropzoneArea from '../DropzoneArea';
import Constants from '../../../../assests/Constants/constants';
import MdbMissingAccountCardsTable from './MdbMissingAccountCardsTable';
import MissingVatReportRowsTable from './MissingVatReportRowsTable';
import useMissingVatReportRows from './useMissingVatReportRows';

const formatAccountDisplay = (id, name) => {
    if (!id && !name) return '—';
    if (id && name) return `${id} (${name})`;
    return id || name || '—';
};

const formatSortCodeDisplay = (id, name) => {
    if (!id && !name) return '—';
    if (id && name) return `${id} (${name})`;
    return id || name || '—';
};

const RamplusJournalImportModal = ({
    open,
    onClose,
    previewRamplusJournal,
    commitRamplusJournal,
    createMissingSortCodes,
    direction,
    t,
    formattedSortCodeVatType = [],
    formattedSortCodesGroupNamesOptions = [],
    client,
    token,
    palCodes = [],
    accountCardOptions = [],
    setAccountCardOptions,
    sortCodeOptions = [],
    fetchEnums,
    setLoading,
    user,
}) => {
    const [journalFile, setJournalFile] = useState(null);
    const [sortCodeFile, setSortCodeFile] = useState(null);
    const [cardsFile, setCardsFile] = useState(null);
    const [vatReportFiles, setVatReportFiles] = useState([]); // { id, file, yearMonth }[]
    const [registrationMonthSource, setRegistrationMonthSource] = useState('DocumentDate');
    const [previewDone, setPreviewDone] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [editableMissing, setEditableMissing] = useState([]);
    const [missingAccountCards, setMissingAccountCards] = useState([]);
    const [skipDuplicates, setSkipDuplicates] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    // Read through a ref so the hook always sees the current choice without re-creating callbacks.
    const registrationMonthSourceRef = useRef(registrationMonthSource);
    registrationMonthSourceRef.current = registrationMonthSource;

    const missingVatRows = useMissingVatReportRows({
        client,
        token,
        t,
        sortCodeOptions,
        getRegistrationMonthSource: useCallback(() => registrationMonthSourceRef.current, []),
    });

    const registrationMonthSourceOptions = useMemo(() => [
        { value: 'DocumentDate', label: t('DocumentDate') || 'תאריך אסמכתא' },
        { value: 'ReportingMonth', label: t('SameAsReportingMonth') || 'זהה לחודש הדיווח' },
    ], [t]);

    const selectedRegistrationMonthSource = useMemo(
        () => registrationMonthSourceOptions.find(o => o.value === registrationMonthSource)
            || registrationMonthSourceOptions[0],
        [registrationMonthSourceOptions, registrationMonthSource]
    );

    const guessVatYearMonthFromFileName = (fileName) => {
        const base = String(fileName || '').replace(/\.[^.]+$/, '');
        const match = base.match(/^(\d{1,2})/);
        if (!match) return '';
        const month = Number(match[1]);
        if (!month || month < 1 || month > 12) return '';
        return `${new Date().getFullYear()}-${String(month).padStart(2, '0')}`;
    };

    const invalidatePreview = () => {
        setPreviewData(null);
        setPreviewDone(false);
        missingVatRows.reset();
    };

    const handleVatReportDrop = (files) => {
        if (!files?.length) return;
        setVatReportFiles(prev => {
            const byName = new Map(prev.map(item => [item.file.name, item]));
            files.forEach(f => {
                const existing = byName.get(f.name);
                byName.set(f.name, {
                    id: existing?.id || `${f.name}-${f.lastModified || Date.now()}`,
                    file: f,
                    yearMonth: existing?.yearMonth || guessVatYearMonthFromFileName(f.name),
                });
            });
            return Array.from(byName.values());
        });
        invalidatePreview();
    };

    const removeVatReportFile = (id) => {
        setVatReportFiles(prev => prev.filter(item => item.id !== id));
        invalidatePreview();
    };

    const updateVatReportYearMonth = (id, yearMonth) => {
        setVatReportFiles(prev => prev.map(item => (item.id === id ? { ...item, yearMonth } : item)));
        invalidatePreview();
    };

    // Omit the files entirely when none are attached so the server keeps the classic import path.
    const buildVatOptions = (registrationSourceOverride) => ({
        registrationMonthSource: registrationSourceOverride || registrationMonthSource,
        ...(vatReportFiles.length > 0 ? { vatReportFiles } : {}),
    });

    const missingIncomplete = useMemo(
        () => editableMissing.some(sc =>
            !sc.sortCodeName?.trim() ||
            sc.sortCodeVatType == null ||
            sc.participatesInProfitAndLoss == null ||
            sc.vat == null
        ),
        [editableMissing]
    );

    const isMissingRowIncomplete = (sc) =>
        !sc.sortCodeName?.trim() ||
        sc.sortCodeVatType == null ||
        sc.participatesInProfitAndLoss == null ||
        sc.vat == null;

    const entries = previewData?.entries ?? [];
    const paginated = entries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const handlePreview = async (registrationSourceOverride) => {
        if (!journalFile || !sortCodeFile) return;
        try {
            setProcessing(true);
            const res = await previewRamplusJournal(
                journalFile, sortCodeFile, cardsFile, buildVatOptions(registrationSourceOverride));
            const missing = (res?.missingSortCodes ?? []).map(sc => ({
                ...sc,
                sortCodeName: sc.sortCodeName?.trim() ? sc.sortCodeName : 'ללא שם',
                sortCodeVatType: sc.sortCodeVatType ?? null,
                sortCodeGroup: sc.sortCodeGroup ?? null,
                participatesInProfitAndLoss: typeof sc.participatesInProfitAndLoss === 'boolean'
                    ? sc.participatesInProfitAndLoss : null,
                vat: typeof sc.vat === 'number' ? sc.vat : null,
            }));
            setEditableMissing(missing);
            setMissingAccountCards(res?.missingAccountCards ?? []);
            // VAT-report extras are only meaningful when VAT files were part of this preview.
            missingVatRows.setRowsFromPreview(vatReportFiles.length > 0 ? (res?.missingVatReportRows ?? []) : []);
            setPreviewData({
                entries: res?.entries ?? [],
                totalLines: res?.parsedRows ?? 0,
                validCount: res?.validCount ?? 0,
                errorCount: res?.errorCount ?? 0,
                duplicateCount: res?.duplicateCount ?? 0,
                excludedByVatReportCount: res?.excludedByVatReportCount ?? 0,
                errorMessages: res?.errorMessages ?? [],
                sortCodeMappings: res?.sortCodeMappings ?? [],
            });
            setPage(0);
            setPreviewDone(true);
        } catch (err) {
            console.error('Ramplus journal preview failed:', err);
            setPreviewDone(false);
        } finally {
            setProcessing(false);
        }
    };

    const handleCommit = async () => {
        if (!journalFile || !sortCodeFile) return;
        try {
            setProcessing(true);
            if (editableMissing.length > 0) {
                await createMissingSortCodes(editableMissing);
                await new Promise(r => setTimeout(r, 500));
            }
            await commitRamplusJournal(journalFile, sortCodeFile, skipDuplicates, cardsFile, buildVatOptions());
            handleClose();
        } catch (err) {
            console.error('Ramplus journal commit error:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleAddVatRows = async (rowIds) => {
        const result = await missingVatRows.addRows(rowIds);
        if (result?.success > 0 && fetchEnums && client && token) {
            try {
                await fetchEnums(token, client, true);
            } catch (err) {
                console.error('Failed to refresh enums after adding VAT rows:', err);
            }
        }
    };

    const updateMissing = (index, field, value) =>
        setEditableMissing(prev => {
            const arr = [...prev];
            arr[index] = { ...arr[index], [field]: value };
            return arr;
        });

    const handleRegistrationMonthSourceChange = async (option) => {
        const value = option?.value || 'DocumentDate';
        setRegistrationMonthSource(value);
        if (previewDone && journalFile && sortCodeFile) {
            await handlePreview(value);
        }
    };

    const handleClose = () => {
        setJournalFile(null);
        setSortCodeFile(null);
        setCardsFile(null);
        setVatReportFiles([]);
        setRegistrationMonthSource('DocumentDate');
        setPreviewDone(false);
        setPreviewData(null);
        missingVatRows.reset();
        setEditableMissing([]);
        setMissingAccountCards([]);
        onClose();
    };

    const formatAmount = (amount) => {
        if (amount == null) return '';
        return new Intl.NumberFormat('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
    };

    if (!open) return null;

    const yesNoOptions = [{ label: t('Yes'), value: true }, { label: t('No'), value: false }];
    const vatOptions = Constants.VATPercentage.map(v => ({ label: `${v}%`, value: v }));

    return (
        <div className="download-modal-overlay" onClick={handleClose}>
            <div className="download-modal" onClick={e => e.stopPropagation()}>
                <div className={`download-modal-content ${direction}`} style={{ position: 'relative', maxWidth: previewDone ? '1200px' : '600px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <h4 className="report-title">רם פלוס - פקודות יומן</h4>
                        <IconButton className="client-back-icon" onClick={handleClose} sx={{ padding: '4px' }}>
                            <CloseIcon />
                        </IconButton>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        <div>
                            <h5>פקודות יומן (.xls)</h5>
                            <DropzoneArea
                                key={journalFile ? journalFile.name : 'ramplus-je-empty'}
                                onDrop={files => files?.length && setJournalFile(files[0])}
                                accept=".xls,.xlsx"
                                filesLimit={1}
                            />
                            {journalFile && <div style={{ marginTop: 8 }}>{t('FileSelected')}: {journalFile.name}</div>}
                        </div>
                        <div>
                            <h5>סוג פקודה / קודי מיון (.xls)</h5>
                            <DropzoneArea
                                key={sortCodeFile ? sortCodeFile.name : 'ramplus-sc-empty'}
                                onDrop={files => files?.length && setSortCodeFile(files[0])}
                                accept=".xls,.xlsx"
                                filesLimit={1}
                            />
                            {sortCodeFile && <div style={{ marginTop: 8 }}>{t('FileSelected')}: {sortCodeFile.name}</div>}
                        </div>
                        <div>
                            <h5>כרטיסי חשבון (.xls) — אופציונלי לשמות כרטיסים חסרים</h5>
                            <DropzoneArea
                                key={cardsFile ? cardsFile.name : 'ramplus-cards-empty'}
                                onDrop={files => files?.length && setCardsFile(files[0])}
                                accept=".xls,.xlsx"
                                filesLimit={1}
                            />
                            {cardsFile && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                                    <span>{t('FileSelected')}: {cardsFile.name}</span>
                                    <Button size="small" variant="text" onClick={() => setCardsFile(null)}>{t('Remove')}</Button>
                                </div>
                            )}
                        </div>
                        <div>
                            <h5>
                                {t('VatReportFiles') || 'קבצי דוח מע״מ חודשיים'} (.xls/.xlsx) - {t('Optional') || 'אופציונלי'}
                            </h5>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {t('VatReportFilesHint') ||
                                    'אופציונלי, ניתן לצרף כמה קבצים ולהוסיף עוד בכל שלב. בלי קבצים — ייבוא רגיל. כשמצורפים: תנועות עם מע״מ ≠ 0 יישמרו רק אם מספר הפקודה (או האסמכתא) קיים בקבצים; חודש הדיווח נלקח מעמודת "חודש מע״מ" יחד עם שנת תאריך האסמכתא. תנועות עם מע״מ 0 ייובאו כרגיל.'}
                            </Typography>
                            <DropzoneArea
                                key={`ramplus-vat-${vatReportFiles.map(item => item.file.name).join('|') || 'empty'}`}
                                onDrop={handleVatReportDrop}
                                accept=".xls,.xlsx"
                                multiple
                            />
                            {vatReportFiles.length > 0 && (
                                <Box mt={1} display="flex" flexDirection="column" gap={1}>
                                    {vatReportFiles.map(item => (
                                        <Box
                                            key={item.id}
                                            display="flex"
                                            flexWrap="wrap"
                                            alignItems="center"
                                            gap={1}
                                            sx={{ p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}
                                        >
                                            <Typography variant="body2" sx={{ flex: 1, minWidth: 160 }}>
                                                {item.file.name}
                                            </Typography>
                                            <TextField
                                                type="month"
                                                size="small"
                                                label={t('VatReportMonthFallback') || 'חודש מע״מ (גיבוי)'}
                                                value={item.yearMonth || ''}
                                                onChange={e => updateVatReportYearMonth(item.id, e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                                helperText={t('VatReportMonthFallbackHint') || 'רק אם אין עמודת חודש מע״מ'}
                                                sx={{ minWidth: 190 }}
                                            />
                                            <Button size="small" variant="text" onClick={() => removeVatReportFile(item.id)}>
                                                {t('Remove')}
                                            </Button>
                                        </Box>
                                    ))}
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={() => { setVatReportFiles([]); invalidatePreview(); }}
                                    >
                                        {t('ClearAll') || 'נקה הכל'}
                                    </Button>
                                </Box>
                            )}
                        </div>
                        <Autocomplete
                            options={registrationMonthSourceOptions}
                            getOptionLabel={o => o?.label ?? ''}
                            value={selectedRegistrationMonthSource}
                            onChange={(_, v) => handleRegistrationMonthSourceChange(v)}
                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                            disableClearable
                            renderInput={params => (
                                <TextField {...params} label={t('RegistrationMonthSource') || 'מקור חודש הרישום'} size="small" />
                            )}
                            sx={{ maxWidth: 320 }}
                        />
                    </div>

                    <div style={{ display: 'flex', gap: 15, marginTop: 20 }}>
                        <Button variant="contained" onClick={() => handlePreview()} disabled={!journalFile || !sortCodeFile || processing}>
                            {t('Preview')}
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            disabled={!previewDone || processing || missingIncomplete || missingAccountCards.length > 0}
                            onClick={handleCommit}
                        >
                            {processing ? t('Processing') : t('CreateAndImport')}
                        </Button>
                    </div>

                    {previewDone && (
                        <Box mt={3} sx={{ maxHeight: 800, overflow: 'auto' }}>
                            {editableMissing.length === 0 ? (
                                <Box mb={2} p={1.5} bgcolor="#d4edda" borderRadius={1} border="1px solid #28a745">
                                    <Typography variant="body2" color="#155724">
                                        ✓ {t('NoMissingSortCodes') || 'אין קודי מיון חסרים'}
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <Typography variant="h6" sx={{ mb: 1 }}>{t('MissingSortCodes')}</Typography>
                                    <table className="sortcode-table editable" style={{ width: '100%', marginBottom: 16 }}>
                                        <thead>
                                            <tr>
                                                <th>{t('SortCode Id')}</th>
                                                <th>{t('SortCode Name')}</th>
                                                <th>{t('SelectSortCodeVatType')}</th>
                                                <th>{t('SelectSortCodeGroupName')}</th>
                                                <th>{t('participatesInProfitAndLoss')}</th>
                                                <th>{t('VAT')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {editableMissing.map((sc, i) => (
                                                <tr
                                                    key={sc.sortCodeId}
                                                    style={{ backgroundColor: isMissingRowIncomplete(sc) ? '#ffebee' : undefined }}
                                                >
                                                    <td>{sc.sortCodeId}</td>
                                                    <td>
                                                        <TextField
                                                            size="small"
                                                            fullWidth
                                                            value={sc.sortCodeName ?? ''}
                                                            onChange={e => updateMissing(i, 'sortCodeName', e.target.value)}
                                                            placeholder={t('SortCode Name')}
                                                            error={!sc.sortCodeName?.trim()}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Autocomplete
                                                            options={formattedSortCodeVatType || []}
                                                            getOptionLabel={o => o?.label ?? ''}
                                                            value={formattedSortCodeVatType.find(o => o.value === sc.sortCodeVatType?.toString()?.trim()) || null}
                                                            onChange={(_, v) => updateMissing(i, 'sortCodeVatType', v?.value ?? null)}
                                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                            renderInput={params => (
                                                                <TextField
                                                                    {...params}
                                                                    size="small"
                                                                    placeholder={t('SelectSortCodeVatType')}
                                                                    error={sc.sortCodeVatType == null}
                                                                />
                                                            )}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Autocomplete
                                                            options={formattedSortCodesGroupNamesOptions || []}
                                                            getOptionLabel={o => o?.label ?? ''}
                                                            value={formattedSortCodesGroupNamesOptions.find(o => o.value === sc.sortCodeGroup) || null}
                                                            onChange={(_, v) => updateMissing(i, 'sortCodeGroup', v?.value ?? null)}
                                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                            renderInput={params => (
                                                                <TextField
                                                                    {...params}
                                                                    size="small"
                                                                    placeholder={t('SelectSortCodeGroupName')}
                                                                />
                                                            )}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Autocomplete
                                                            options={yesNoOptions}
                                                            getOptionLabel={o => o?.label ?? ''}
                                                            value={sc.participatesInProfitAndLoss === true ? yesNoOptions[0]
                                                                : sc.participatesInProfitAndLoss === false ? yesNoOptions[1] : null}
                                                            onChange={(_, v) => updateMissing(i, 'participatesInProfitAndLoss', v?.value ?? null)}
                                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                            renderInput={params => (
                                                                <TextField
                                                                    {...params}
                                                                    size="small"
                                                                    placeholder={t('participatesInProfitAndLoss')}
                                                                    error={sc.participatesInProfitAndLoss == null}
                                                                />
                                                            )}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Autocomplete
                                                            options={vatOptions}
                                                            getOptionLabel={o => o?.label ?? ''}
                                                            value={vatOptions.find(o => o.value === sc.vat) || null}
                                                            onChange={(_, v) => updateMissing(i, 'vat', v?.value ?? null)}
                                                            isOptionEqualToValue={(a, b) => a?.value === b?.value}
                                                            renderInput={params => (
                                                                <TextField
                                                                    {...params}
                                                                    size="small"
                                                                    placeholder={t('VAT')}
                                                                    error={sc.vat == null}
                                                                />
                                                            )}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            )}

                            {missingAccountCards.length > 0 && (
                                <Box mt={2.5} mb={2}>
                                    <MdbMissingAccountCardsTable
                                        missingAccountCards={missingAccountCards}
                                        direction={direction}
                                        client={client}
                                        token={token}
                                        palCodes={palCodes || []}
                                        accountCardOptions={accountCardOptions}
                                        fetchEnums={fetchEnums}
                                        setLoading={setLoading}
                                        isRange={false}
                                        isClosedReportingMonth={false}
                                        sortCodes={[]}
                                        user={user}
                                        setAccountCardOptions={setAccountCardOptions}
                                        onCardCreated={async () => {
                                            if (journalFile && sortCodeFile) {
                                                await handlePreview();
                                            }
                                        }}
                                    />
                                </Box>
                            )}

                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {t('TotalLines')}: <strong>{previewData?.totalLines}</strong>
                                {' | '}{t('ValidCount')}: <strong>{previewData?.validCount}</strong>
                                {' | '}{t('ErrorCount')}: <strong>{previewData?.errorCount}</strong>
                                {(previewData?.duplicateCount ?? 0) > 0 && (
                                    <>{' | '}<span style={{ color: '#ed6c02' }}>{t('Duplicates')}: <strong>{previewData.duplicateCount}</strong></span></>
                                )}
                                {(previewData?.excludedByVatReportCount ?? 0) > 0 && (
                                    <>{' | '}<span style={{ color: '#9c27b0' }}>
                                        {t('ExcludedByVatReport') || 'לא נמצא בדוח מע״מ'}: <strong>{previewData.excludedByVatReportCount}</strong>
                                    </span></>
                                )}
                            </Typography>

                            {vatReportFiles.length > 0 && (
                                <Box mb={2.5}>
                                    <MissingVatReportRowsTable
                                        rows={missingVatRows.rows}
                                        selectedIds={missingVatRows.selectedIds}
                                        onSelectedChange={missingVatRows.setSelectedIds}
                                        adding={missingVatRows.adding}
                                        onUpdateRow={missingVatRows.updateRow}
                                        onAddRows={handleAddVatRows}
                                        sortCodeOptions={sortCodeOptions}
                                        accountCardOptions={accountCardOptions}
                                        t={t}
                                        title={t('MissingVatReportRows') || 'שורות מדוח מע״מ שלא קיימות בקובץ פקודות היומן'}
                                        emptyMessage={t('NoMissingVatReportRows') || 'כל שורות דוח המע״מ נמצאו בקובץ פקודות היומן'}
                                        formatAmount={formatAmount}
                                    />
                                </Box>
                            )}

                            {(previewData?.duplicateCount ?? 0) > 0 && (
                                <FormControlLabel
                                    control={<Checkbox checked={skipDuplicates} onChange={e => setSkipDuplicates(e.target.checked)} size="small" />}
                                    label={<Typography variant="body2">{t('SkipDuplicateEntries')}</Typography>}
                                />
                            )}

                            {paginated.length > 0 && (
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small" stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>{t('Line')}</TableCell>
                                                <TableCell>{t('TransactionNumber') || 'מספר תנועה'}</TableCell>
                                                <TableCell>{t('DocumentDate')}</TableCell>
                                                <TableCell>{t('ReportingMonth')}</TableCell>
                                                <TableCell>{t('RegistrationMonth')}</TableCell>
                                                <TableCell>{t('Reference')}</TableCell>
                                                <TableCell>{t('DebitAccount')}</TableCell>
                                                <TableCell>{t('CreditAccount')}</TableCell>
                                                <TableCell align="right">{t('Amount')}</TableCell>
                                                <TableCell>{t('SortCode Id')}</TableCell>
                                                <TableCell>{t('Description')}</TableCell>
                                                <TableCell>{t('Status')}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {paginated.map((row, i) => {
                                                const hasMissing =
                                                    row.debitAccountMissing ||
                                                    row.creditAccountMissing ||
                                                    row.sortCodeMissing;
                                                const excludedByVatReport = row.errorMessage === 'ExcludedByVatReport';
                                                return (
                                                    <TableRow
                                                        key={row.lineNumber ?? i}
                                                        sx={{
                                                            bgcolor: hasMissing
                                                                ? '#ffebee'
                                                                : excludedByVatReport
                                                                    ? '#f3e5f5'
                                                                    : row.isDuplicate
                                                                        ? '#fff3e0'
                                                                        : 'transparent',
                                                            opacity: (row.isDuplicate && skipDuplicates) || excludedByVatReport ? 0.5 : 1,
                                                        }}
                                                    >
                                                        <TableCell>{row.lineNumber}</TableCell>
                                                        <TableCell>{row.transactionNumber ?? '—'}</TableCell>
                                                        <TableCell>{row.documentDate ?? '—'}</TableCell>
                                                        <TableCell>{row.reportingMonth ?? '—'}</TableCell>
                                                        <TableCell>{row.registrationMonth ?? '—'}</TableCell>
                                                        <TableCell>{row.reference ?? '—'}</TableCell>
                                                        <TableCell sx={{
                                                            color: row.debitAccountMissing ? 'error.main' : 'inherit',
                                                            fontWeight: row.debitAccountMissing ? 600 : 400,
                                                            maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word',
                                                        }}>
                                                            {formatAccountDisplay(row.debitAccount, row.debitAccountName)}
                                                        </TableCell>
                                                        <TableCell sx={{
                                                            color: row.creditAccountMissing ? 'error.main' : 'inherit',
                                                            fontWeight: row.creditAccountMissing ? 600 : 400,
                                                            maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word',
                                                        }}>
                                                            {formatAccountDisplay(row.creditAccount, row.creditAccountName)}
                                                        </TableCell>
                                                        <TableCell align="right">{formatAmount(row.amount)}</TableCell>
                                                        <TableCell sx={{
                                                            color: row.sortCodeMissing ? 'error.main' : 'inherit',
                                                            fontWeight: row.sortCodeMissing ? 600 : 400,
                                                            maxWidth: 180, whiteSpace: 'normal', wordBreak: 'break-word',
                                                        }}>
                                                            {formatSortCodeDisplay(row.sortCodeId, row.sortCodeName)}
                                                        </TableCell>
                                                        <TableCell>{row.description ?? ''}</TableCell>
                                                        <TableCell>
                                                            {excludedByVatReport
                                                                ? <Typography variant="body2" sx={{ color: '#9c27b0' }}>
                                                                    {t('ExcludedByVatReport') || 'לא נמצא בדוח מע״מ'}
                                                                </Typography>
                                                                : row.isDuplicate
                                                                    ? <Typography variant="body2" color="warning.main">{t('Duplicate')}</Typography>
                                                                    : row.isValid
                                                                        ? <Typography variant="body2" color="success.main">{t('Valid')}</Typography>
                                                                        : <Typography variant="body2" color="error.main" title={row.errorMessage}>{row.errorMessage ?? t('Error')}</Typography>}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                    <TablePagination
                                        component="div"
                                        count={entries.length}
                                        page={page}
                                        onPageChange={(_, p) => setPage(p)}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                                        rowsPerPageOptions={[10, 25, 50, 100]}
                                        labelRowsPerPage={t('RowsPerPage')}
                                    />
                                </TableContainer>
                            )}
                        </Box>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RamplusJournalImportModal;
